import { useQuery } from '@tanstack/react-query';
import type {
  DashboardMetricKey,
  DashboardStatsResponse,
  DashboardMetricError,
} from '@vagusx/clients';
import { getDashboardClient } from '../lib/api-clients';
import { queryKeys } from '../lib/query-keys';

/**
 * Single-metric React Query hook. Each tile calls this for its own
 * metric so per-tile loading state is independent — a slow metric
 * (e.g. `time_to_resolution_by_label` running PERCENTILE_CONT) doesn't
 * skeletonize the rest of the dashboard.
 *
 * Implementation: each call hits `/api/dashboard/stats?metrics=<key>`
 * separately. The backend orchestrator's per-fetcher TTL cache absorbs
 * the per-tile request fan-out — repeat hits within the cache window
 * are O(1) lookups. Tiles that share a metric (rare today, but the
 * shape allows it) still share cache via React Query's queryKey.
 *
 * Returns the same loading/error/data envelope as `useQuery`. The
 * extracted `metric` value lives at `data?.metrics[key]`; the
 * extracted `error` envelope (server-side fetcher failure) lives at
 * `data?.errors?.[key]`.
 */
export function useDashboardMetric<K extends DashboardMetricKey>(opts: {
  metricKey: K;
  from?: string;
  to?: string;
  /** When false, the query is disabled — used in edit mode. */
  enabled?: boolean;
}): {
  metric: DashboardStatsResponse['metrics'][K];
  fetcherError: DashboardMetricError | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery<DashboardStatsResponse>({
    queryKey: queryKeys.dashboard.stats([opts.metricKey], opts.from, opts.to),
    queryFn: () =>
      getDashboardClient().getStats({
        metrics: [opts.metricKey],
        from: opts.from,
        to: opts.to,
      }),
    staleTime: 60 * 1000,
    enabled: opts.enabled ?? true,
  });

  return {
    metric: query.data?.metrics[opts.metricKey],
    fetcherError: query.data?.errors?.[opts.metricKey],
    // Server-side per-fetcher failure (response carries an `errors`
    // envelope but the rest of the request succeeded) is treated as
    // a tile-level error so the tile renders an error state.
    isLoading: query.isLoading,
    isError: query.isError || Boolean(query.data?.errors?.[opts.metricKey]),
  };
}
